import { Capacitor } from '@capacitor/core'
import { IonSpinner } from '@ionic/react'
import dayjs from 'dayjs'
import React, { FC, useContext, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { logEvent } from '../../../analytics/utils/firebaseUtils'
import Button from '../../../components/actions/Button'
import { decodeBase64 } from '../../../components/mainTabs/WebAppLayout'
import Stepper, { StepConfig } from '../../../components/stepper/Stepper'
import { ModalContext } from '../../../contexts/ModalContext'
import { ObjectContext } from '../../../contexts/objectContext/ObjectContext'
import { UserContext } from '../../../contexts/UserContext'
import { trpc } from '../../../modules/trpc'
import PageWrapper from '../../../wrappers/pageWrapper/PageWrapper'
import AuthenticateStep from '../moveOutWizard/steps/AuthenticateStep'
import AdditionalDetailsStep from './steps/AdditionalDetailsStep'
import ChooseInvoiceStep from './steps/ChooseInvoiceStep'
import NewInvoiceDueDateStep from './steps/NewInvoiceDueDateStep'

export interface InvoiceExtensionFormFields {
	invoiceId: string | null
	currentDueDate: string
	newDueDate: string
	additionalDetails: string
}

const InvoiceExtensionPage: FC = () => {
	const { mutateAsync: createInvoiceExtension } =
		trpc.contact.createInvoiceExtension.useMutation<string>()

	const { insertModal } = useContext(ModalContext)
	const { authExpire, setPostAuthRedirect } = useContext(UserContext)
	const object = useContext(ObjectContext)

	const { t: tc } = useTranslation('pages_invoiceExtension')
	const { t } = useTranslation('pages_contractConfirmation')
	const { t: tcm } = useTranslation('modals_moveOutContact')

	const [activeStep, setActiveStep] = useState(0)
	const [isSending, setIsSending] = useState<boolean>(false)

	const form = useForm<InvoiceExtensionFormFields>({
		mode: 'onBlur',
		defaultValues: {
			newDueDate: '',
			currentDueDate: '',
			additionalDetails: '',
			invoiceId: null,
		},
	})

	const handleNext = async () => {
		logEvent(`click-invoice-extension-next-step-${activeStep + 1}`)
		setActiveStep(prevActiveStep => prevActiveStep + 1)
	}

	const handlePrev = () => {
		logEvent(`click-invoice-extension-previous-step-${activeStep - 1}`)
		setActiveStep(prevActiveStep => prevActiveStep - 1)
	}

	const steps: StepConfig<InvoiceExtensionFormFields>[] = [
		{
			label: tcm('authenticateStep.authenticate'),
			key: 'AuthenticateStep',
			element: <AuthenticateStep redirectUri='/contact/invoice-extension' />,
			ref: useRef<HTMLDivElement>(null),
			animationMs: 70,
			disabled:
				Capacitor.isNativePlatform() ||
				(authExpire !== null && dayjs(decodeBase64(String(authExpire))).isAfter(dayjs())),
			disableNext: () => true,
			hideNext: true,
		},
		{
			label: tc('chooseInvoiceStep.title'),
			key: 'ChooseInvoiceStep',
			element: <ChooseInvoiceStep form={form} />,
			ref: useRef<HTMLDivElement>(null),
			animationMs: 70,
			disableNext: () => !form.watch('invoiceId'),
			disabled: false,
		},
		{
			label: tc('newInvoiceDueDateStep.title'),
			key: 'NewInvoiceDueDateStep',
			element: <NewInvoiceDueDateStep form={form} />,
			ref: useRef<HTMLDivElement>(null),
			animationMs: 70,
			disabled: false,
		},
		{
			label: tcm('additionalDetailsStep.title'),
			key: 'AdditionalDetailsStep',
			element: <AdditionalDetailsStep form={form} />,
			ref: useRef<HTMLDivElement>(null),
			animationMs: 70,
			disabled: false,
		},
	]

	const handleSubmit = async () => {
		setIsSending(true)
		const { invoiceId, newDueDate, currentDueDate, additionalDetails } = form.getValues()

		if (process.env.react_app_environment !== 'LOCAL') {
			await createInvoiceExtension({
				objectId: object.id,
				invoiceId: invoiceId ?? '',
				newDueDate: newDueDate,
				currentDueDate: currentDueDate,
				additionalDetails: additionalDetails,
			})
			setIsSending(false)
			setPostAuthRedirect('contact-confirmation')
			insertModal('contact-confirmation')
		}
		setIsSending(false)
	}

	return (
		<PageWrapper page='invoiceExtension' appBarOptions={{ navigationType: 'back' }}>
			<form
				id='get-our-contract'
				onSubmit={handleSubmit}
				style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
			>
				<Stepper
					steps={steps}
					activeStep={activeStep}
					onNext={handleNext}
					onPrev={handlePrev}
					form={form}
					lastStepAction={
						<Button disabled={isSending} onClick={handleSubmit}>
							{isSending ? <IonSpinner /> : t('valmis', { ns: 'common' })}
						</Button>
					}
				/>
			</form>
		</PageWrapper>
	)
}

export default InvoiceExtensionPage
